import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import { MAX_PROJECT_NAME_LENGTH } from "../model/storage.ts";
import { DropdownMenu } from "./DropdownMenu.tsx";
import { DuplicateIcon } from "./DuplicateIcon.tsx";
import { CloseIcon } from "./CloseIcon.tsx";
import { WarningIcon } from "./WarningIcon.tsx";
import { Icon } from "./Icon.tsx";
import { IconButton } from "./IconButton.tsx";
import { TextInputDialog } from "./TextInputDialog.tsx";

/**
 * Reminder that a sample project's edits aren't saved unless you make a
 * copy — always PRESENT while a sample is open, never fully dismissible
 * (own header's original reasoning still holds: most people lose a
 * sample's edits by closing the browser tab directly, never touching an
 * in-app "close" button). What changed (live feedback, second audit):
 * the full card used to live in the top-center "state banners" slot,
 * where it collided with map-hint banners (measure/trace/longitudinal-
 * profile picking) sharing that same real estate. Moved next to the map's
 * own zoom controls instead — a corner nothing else claims — and made
 * collapsible: the "x" shrinks it to just the warning icon (still
 * clickable, still there), not gone. Clicking the icon expands it back.
 */
export function ExampleBanner({
  projectName,
  onSaveCopy,
  attribution,
  sourceUrl,
  license,
}: {
  projectName: string;
  onSaveCopy: (name: string) => void;
  /** Set only for a sample whose license requires attribution when redistributed (GPL-3.0 ones, today). */
  attribution?: string;
  sourceUrl?: string | null;
  /** SPDX short identifier (e.g. `GPL-3.0`) — surfaced in the compact chip
   *  next to the main message. The full `attribution` text lives one click
   *  away behind the info button's popover so the card doesn't grow past
   *  a reasonable width on samples with a long attribution line. */
  license?: string | null;
}) {
  const t = useT();
  const [showDialog, setShowDialog] = useState(false);
  const [attributionOpen, setAttributionOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  if (collapsed) {
    return (
      <div className="example-banner-slot">
        <IconButton
          className="example-banner-icon"
          icon={<WarningIcon />}
          label={t("exampleBanner.message")}
          onClick={() => setCollapsed(false)}
        />
      </div>
    );
  }

  return (
    <div className="example-banner-slot">
      <div className="example-banner">
        <WarningIcon />
        <span>{t("exampleBanner.message")}</span>
        {license && attribution && (
          <span className="example-banner-license">
            <span aria-hidden="true">·</span>
            <span>{license}</span>
            <DropdownMenu
              open={attributionOpen}
              onToggle={() => setAttributionOpen((open) => !open)}
              onClose={() => setAttributionOpen(false)}
              triggerClassName="button-icon example-banner-info-button"
              triggerLabel={t("exampleBanner.attribution")}
              triggerIcon={
                <Icon>
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="11" x2="12" y2="16" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                </Icon>
              }
              popoverClassName="popover example-banner-attribution-popover"
              closeOnLeave={false}
            >
              <p className="example-banner-attribution">
                {attribution}
                {sourceUrl && (
                  <>
                    {" "}
                    <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                      {t("exampleBanner.sourceLink")}
                    </a>
                  </>
                )}
              </p>
            </DropdownMenu>
          </span>
        )}
        <button className="button button-primary" onClick={() => setShowDialog(true)}>
          <DuplicateIcon />
          {t("projectMenu.duplicate")}
        </button>
        <button
          className="button-icon example-banner-collapse"
          onClick={() => setCollapsed(true)}
          aria-label={t("common.close")}
          title={t("common.close")}
        >
          <CloseIcon />
        </button>
      </div>
      {showDialog && (
        <TextInputDialog
          title={t("projectMenu.duplicateTitle")}
          initialValue={t("projectMenu.copyNameSuffix", { name: projectName })}
          maxLength={MAX_PROJECT_NAME_LENGTH}
          onSave={(name) => {
            onSaveCopy(name);
            setShowDialog(false);
          }}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
}
